import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { DEFAULT_REVIEW_URL } from "@/lib/useGoogleReviews";
import { HoursList } from "./HoursList";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";
import { BookButton } from "./BookButton";

export function Footer() {
  const { settings } = useSite();
  const s = settings || {};
  return (
    <footer data-testid="site-footer" className="bg-espresso text-ivory">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            {(s.logo_light_url || s.logo_url) && (
              <img src={resolveImg(s.logo_light_url || s.logo_url)} alt="Psalm 30:2 Chiropractic" className="h-16 w-auto object-contain mb-8" style={{ filter: s.logo_light_url ? "none" : "invert(1)" }} data-testid="footer-logo" />
            )}
            <div className="font-serif text-4xl lg:text-5xl leading-tight mb-6">
              Get back to<br />what matters.
            </div>
            <BookButton label="Book Your Visit" variant="brass" track_label="footer" testid="footer-book-button" />
            {s.scripture && (
              <p className="mt-12 font-serif italic text-lg text-ivory/70 max-w-sm">
                "{s.scripture}"
                <span className="block not-italic text-xs uppercase tracking-[0.2em] mt-3 text-ivory/40">{s.scripture_ref}</span>
              </p>
            )}
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <div className="overline !text-brass mb-5">Explore</div>
            <ul className="space-y-3 text-ivory/70 text-sm">
              <li><Link to="/approach" className="link-underline">Our Approach</Link></li>
              <li><Link to="/nervous-system" className="link-underline">Nervous System</Link></li>
              <li><Link to="/treatments" className="link-underline">Treatments</Link></li>
              <li><Link to="/conditions" className="link-underline">Where Do You Need Relief?</Link></li>
              <li><Link to="/family-care" className="link-underline">Family Care</Link></li>
              <li><Link to="/pregnancy" className="link-underline">Pregnancy & Birth</Link></li>
              <li><Link to="/pediatric" className="link-underline">Pediatric Care</Link></li>
              <li><Link to="/patient-stories" className="link-underline">Patient Stories</Link></li>
              <li><Link to="/about" className="link-underline">About</Link></li>
              <li><Link to="/learn" className="link-underline">Learn</Link></li>
              <li><Link to="/memberships" className="link-underline">Memberships</Link></li>
              <li><Link to="/reviews" className="link-underline" data-testid="footer-reviews-link">Reviews</Link></li>
              <li><a href={s.google_review_url || DEFAULT_REVIEW_URL} target="_blank" rel="noreferrer" className="link-underline text-brass" data-testid="footer-leave-review">Leave a Google review</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline !text-brass mb-5">Visit</div>
            <div className="space-y-4 text-ivory/70 text-sm">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(s.map_query || "")}`} target="_blank" rel="noreferrer" className="flex gap-3 items-start hover:text-ivory" data-testid="footer-address">
                <MapPin size={18} className="mt-0.5 shrink-0 text-brass" strokeWidth={1.5} />
                <span>{s.address_line1}<br />{s.address_line2}</span>
              </a>
              {s.phone && (
                <a href={`tel:${s.phone}`} className="flex gap-3 items-center hover:text-ivory" data-testid="footer-phone">
                  <Phone size={18} className="text-brass" strokeWidth={1.5} /> {s.phone}
                </a>
              )}
              {s.email && (
                <a href={`mailto:${s.email}`} className="flex gap-3 items-center hover:text-ivory" data-testid="footer-email">
                  <Mail size={18} className="text-brass" strokeWidth={1.5} /> {s.email}
                </a>
              )}
              <div className="pt-2">
                <div className="text-xs uppercase tracking-[0.15em] text-ivory/40 mb-2">Hours</div>
                <div className="max-w-[270px]">
                  <HoursList hours={s.hours} rowClass="py-0.5" valueClass="text-ivory/55" testid="footer-hours" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-ivory/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-ivory/40 uppercase tracking-[0.12em]">
          <span>© {new Date().getFullYear()} Psalm 30:2 Chiropractic · Gardner, Kansas</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-ivory">Privacy</Link>
            <Link to="/terms" className="hover:text-ivory">Terms</Link>
            <Link to="/accessibility" className="hover:text-ivory">Accessibility</Link>
            <Link to="/login" className="hover:text-ivory" data-testid="footer-admin-link">Staff Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
